<template>
  <div>
    <label class="input-label" v-if="label">{{ label }}</label>
    <v-text-field
      ref="addressInput"
      :value="input" 
      @input="$emit('input', $event)"
      :label="label ? '' : 'Enter address'"
      :dense="dense"
      :outlined="outlined"
      prepend-inner-icon="mdi-map-marker"
      :rules="getRules"
      v-bind="$attrs"
      :class="label ? 'mt-1' : ''"
      id="address-field-input"
    >
      <template v-slot:append>
        <v-tooltip bottom v-if="help">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="" x-small>
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ help }}</span>
        </v-tooltip>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { getRules } from "@/utils/common.utils";

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      default: "",
    },
    help: {
      type: String,
      default: "",
    },
    dense: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: String,
      default: "",
    },
    customRules: {
      type: Array,
      default: () => [],
    },
    autocompleteOptions: {
      type: Object,
      default: () => ({
        fields: ["address_components", "formatted_address", "geometry", "name", "place_id"],
        types: ["address"],
      }),
    },
  },
  data() {
    return {
      autocomplete: null,
    };
  },
  computed: {
    getRules() {
      let { customRules, rules } = this;
      if (customRules.length > 0) return customRules;
      return getRules(rules);
    },
    input: {
        get() {
            return this.value;
        },
        set(val) {
            this.$emit('input', val);
        }
    }
  },
  watch: {
  },
  mounted() {
    this.initAutocomplete();
  },
  methods: {
    initAutocomplete() {
      this.$nextTick(async () => {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.warn("Google Maps Places API not loaded for AddressField.vue");
          return;
        }

        const inputElement = this.$refs.addressInput?.$el?.querySelector('input');
        if (!inputElement) {
          console.error("AddressField.vue: Input element not found for Autocomplete.");
          return;
        }
        
        try {
          this.autocomplete = new window.google.maps.places.PlaceAutocompleteElement({
            inputElement: inputElement,
            ...this.autocompleteOptions,
          });

          this.autocomplete.addEventListener("gmp-placeselect", async (event) => {
            const place = event.place;
            if (!place || !place.id) {
              this.$emit("onAddress", null, null);
              return;
            }
            
            const fieldsToFetch = this.autocompleteOptions.fields || ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'];
            if (place.fetchFields) {
                 await place.fetchFields({ fields: fieldsToFetch });
            }

            const addressData = {
              street_number: place.addressComponents?.find(c => c.types.includes('street_number'))?.longText || '',
              route: place.addressComponents?.find(c => c.types.includes('route'))?.longText || '',
              locality: place.addressComponents?.find(c => c.types.includes('locality'))?.longText || '',
              administrative_area_level_1: place.addressComponents?.find(c => c.types.includes('administrative_area_level_1'))?.shortText || '',
              country: place.addressComponents?.find(c => c.types.includes('country'))?.longText || '',
              postal_code: place.addressComponents?.find(c => c.types.includes('postal_code'))?.longText || '',
              latitude: place.geometry?.location?.lat(),
              longitude: place.geometry?.location?.lng(),
              name: place.name,
              formatted_address: place.formattedAddress,
              place_id: place.id,
            };
            
            this.$emit('input', place.formattedAddress);

            this.$emit("onAddress", addressData, place);
          });
        } catch (error) {
            console.error("Error initializing PlaceAutocompleteElement in AddressField:", error);
        }
      });
    },
  },
};
</script>
